import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TradeEntry } from '../../models/trade-entry';
import { DEFAULT_RISK_PROFILE, DEFAULT_RISK_STEPS, RiskProfile, RiskStep } from '../../models/risk-setup';
import { ApiService } from '../../services/api.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { RiskBudgetService, RiskSummary } from '../../services/risk-budget.service';

@Component({
  selector: 'app-risk-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './risk-management.component.html',
  styleUrls: ['./risk-management.component.scss']
})
export class RiskManagementComponent implements OnInit, OnDestroy {
  profileForm: FormGroup;
  stepsForm: FormArray<FormGroup>;
  stepsError = '';
  summary = signal<{ riskPerTrade: number; totalBudget: number; appliedLabel?: string }>({ riskPerTrade: 0, totalBudget: 0 });
  tradesNeeded = signal<number | null>(null);
  private lastSummary: RiskSummary | null = null;
  yearOptions: number[] = [];
  hasProfiles = true;
  newProfileOpen = false;
  newProfileYear = '';
  newProfileError = '';
  private stepsDirty = false;
  private currentProfileId = '';
  private suppressSaves = false;

  private subscriptions = new Subscription();

  constructor(private fb: FormBuilder, private api: ApiService, private riskBudget: RiskBudgetService) {
    this.stepsForm = this.fb.array<FormGroup>([]);
    this.profileForm = this.fb.group({
      maxLossThresholdEUR: [DEFAULT_RISK_PROFILE.maxLossThresholdEUR, [Validators.required, Validators.min(0)]],
      baseRiskPerTradeEUR: [DEFAULT_RISK_PROFILE.baseRiskPerTradeEUR, [Validators.required, Validators.min(0)]],
      riskRewardRatio: [DEFAULT_RISK_PROFILE.riskRewardRatio, [Validators.required, Validators.min(0.1)]],
      winProbabilityPct: [DEFAULT_RISK_PROFILE.winProbabilityPct, [Validators.required, Validators.min(0), Validators.max(100)]],
      targetGoalEUR: [DEFAULT_RISK_PROFILE.targetGoalEUR, [Validators.required, Validators.min(0)]],
      planYear: [DEFAULT_RISK_PROFILE.planYear, [Validators.required, Validators.min(2025)]],
    });
    this.yearOptions = this.buildYearOptions();
  }

  async ngOnInit(): Promise<void> {
    await this.loadPlanYears();
    if (this.hasProfiles) {
      await this.loadProfile(this.selectedPlanYear);
      await this.loadSteps(this.profileId);
    }
    this.updateWinProbabilityFromTrades();

    this.subscriptions.add(
      this.profileForm.valueChanges.subscribe(() => {
        if (this.suppressSaves) return;
        this.broadcastProfile();
        this.saveProfile(); 
      })
    );
    this.subscriptions.add(
      this.stepsForm.valueChanges.subscribe(() => {
        if (this.suppressSaves) return;
        this.stepsDirty = true;
        this.broadcastProfile();
      })
    );
    this.subscriptions.add(
      this.api.tradeChanges$.subscribe(() => {
        this.updateWinProbabilityFromTrades();
      })
    );

    this.subscriptions.add(
      this.riskBudget.summary$.subscribe((summary) => {
        this.lastSummary = summary;
        const appliedLabel = summary.appliedStep
          ? `ab ${summary.appliedStep.profitMinEUR} → ${summary.appliedStep.riskPerTradeEUR}`
          : undefined;
        this.summary.set({
          riskPerTrade: summary.riskPerTradeEUR,
          totalBudget: summary.totalRiskBudgetEUR,
          appliedLabel,
        });
        this.recalculateTradesNeeded();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get stepControls(): FormGroup[] {
    return this.stepsForm.controls as unknown as FormGroup[];
  }

  addStep(step?: RiskStep, persist = true): void {
    const group = this.fb.group({
      id: [step?.id ?? null],
      profitMinEUR: [step?.profitMinEUR ?? 0, [Validators.required, Validators.min(0)]],
      riskPerTradeEUR: [step?.riskPerTradeEUR ?? 0, [Validators.required, Validators.min(0)]],
    });
    this.stepsForm.push(group);
    if (!this.suppressSaves && persist) {
      this.saveStep(group.value as RiskStep);
    }
  }

  removeStep(index: number): void {
    if (index === this.stepsForm.length - 1 && this.stepsForm.length > 1) {
      const step = this.stepsForm.at(index).value as RiskStep;
      this.stepsForm.removeAt(index);
      if (step.id) {
        this.deleteStepRemote(step.id);
      }
      this.broadcastProfile();
    }
  }

  autoExtendSteps(): void {
    const sorted = this.sortSteps(this.readProfile().riskSteps);
    const last = sorted[sorted.length - 1];
    const prev = sorted.length > 1 ? sorted[sorted.length - 2] : undefined;
    const deltaProfit = prev ? Math.max(last.profitMinEUR - prev.profitMinEUR, 1) : 2500;
    const deltaRisk = prev ? Math.max(last.riskPerTradeEUR - prev.riskPerTradeEUR, 1) : 25;
    const nextStep: RiskStep = {
      profitMinEUR: last.profitMinEUR + deltaProfit,
      riskPerTradeEUR: last.riskPerTradeEUR + deltaRisk,
    };

    this.addStep(nextStep);
    this.broadcastProfile();
  }

  async saveProfile(): Promise<void> {
    const rawProfile = this.readProfile();

    try {
      const normalized = { ...rawProfile, riskSteps: this.sortSteps(rawProfile.riskSteps) };
      let savedSteps = normalized.riskSteps;
      if (this.stepsDirty) {
        if (this.validateSteps(normalized.riskSteps)) {
          savedSteps = await this.persistSteps(normalized.riskSteps);
          this.stepsDirty = false;
        }
      }
      const { riskSteps, ...profilePayload } = normalized;
      const savedProfile = await firstValueFrom(this.api.updateRiskProfile(profilePayload as RiskProfile));
      const updatedProfile = savedProfile ? { ...savedProfile, riskSteps: savedSteps } : { ...normalized, riskSteps: savedSteps };
      this.applyProfile(updatedProfile);
      this.recalculateTradesNeeded();
    } catch (error) {
      // keep current values and let journal error handler surface issues
    }
  }

  async onStepBlur(index: number): Promise<void> {
    const step = this.stepsForm.at(index).value as RiskStep;
    if (this.suppressSaves) return;
    await this.saveStep(step);
  }

  private async saveStep(step: RiskStep): Promise<void> {
    if (!this.validateSteps([step], false)) return;
    const profileId = this.profileId;
    try {
      let saved: RiskStep | null = null;
      if (step.id) {
        saved = await firstValueFrom(this.api.updateRiskStep(step.id, step));
      } else {
        saved = await firstValueFrom(this.api.createRiskStep(step, profileId));
      }
      if (saved) {
        this.stepsForm.controls = this.stepsForm.controls.map((ctrl) => {
          const current = ctrl.value as RiskStep;
          if (current === step || current.id === saved?.id) {
            ctrl.patchValue({ id: saved.id, profitMinEUR: saved.profitMinEUR, riskPerTradeEUR: saved.riskPerTradeEUR }, { emitEvent: false });
          }
          return ctrl;
        }) as any;
      }
      this.broadcastProfile();
    } catch {
      // ignore failure for now
    }
  }

  private broadcastProfile(): void {
    const raw = this.readProfile();
    const profile = { ...raw, riskSteps: this.sortSteps(raw.riskSteps) };
    if (!this.validateSteps(profile.riskSteps, false)) return;
    this.riskBudget.nextProfileFromForm(profile);
    this.recalculateTradesNeeded();
  }

  private async updateWinProbabilityFromTrades(): Promise<void> {
    try {
      const trades = await firstValueFrom(this.api.getTrades());
      const realized = (trades ?? []).filter((entry) => entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0);
      const wins = realized.filter((entry) => this.realizedPnl(entry) > 0).length;
      const losses = realized.filter((entry) => this.realizedPnl(entry) < 0).length;
      const total = wins + losses;
      const winPct = total > 0 ? +((wins / total) * 100).toFixed(1) : 0;
      this.profileForm.patchValue({ winProbabilityPct: winPct }, { emitEvent: false });
      this.broadcastProfile();
    } catch {
      // ignore trade lookup failures
    }
  }

  private realizedPnl(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      return entry.partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
    }
    return entry.realizedPnl ?? 0;
  }

  private readProfile(): RiskProfile {
    const steps: RiskStep[] = this.stepsForm.controls.map((ctrl) => ({
      id: ctrl.value.id ?? undefined,
      profitMinEUR: Number(ctrl.value.profitMinEUR ?? 0),
      riskPerTradeEUR: Number(ctrl.value.riskPerTradeEUR ?? 0),
    }));

    return {
      id: this.profileId,
      maxLossThresholdEUR: Number(this.profileForm.value.maxLossThresholdEUR ?? 0),
      baseRiskPerTradeEUR: Number(this.profileForm.value.baseRiskPerTradeEUR ?? 0),
      riskSteps: steps,
      riskRewardRatio: Number(this.profileForm.value.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio ?? 0),
      winProbabilityPct: Number(this.profileForm.value.winProbabilityPct ?? DEFAULT_RISK_PROFILE.winProbabilityPct ?? 0),
      targetGoalEUR: Number(this.profileForm.value.targetGoalEUR ?? DEFAULT_RISK_PROFILE.targetGoalEUR ?? 0),
      planYear: Number(this.profileForm.value.planYear ?? DEFAULT_RISK_PROFILE.planYear ?? new Date().getFullYear()),
    };
  }

  private validateSteps(steps: RiskStep[], showError = true): boolean {
    let valid = true;
    let message = '';

    steps.forEach((step) => {
      if (step.profitMinEUR < 0 || step.riskPerTradeEUR < 0) {
        valid = false;
        message = 'Werte müssen ≥ 0 sein.';
      }
    });

    const isSorted = steps.every((step, index) => index === 0 || step.profitMinEUR >= steps[index - 1].profitMinEUR);
    if (!isSorted) {
      valid = false;
      message = 'Stufen müssen aufsteigend sortiert sein.';
    }

    this.stepsError = showError ? message : '';
    return valid;
  }

  private applyProfile(profile: RiskProfile): void {
    this.profileForm.patchValue({
      maxLossThresholdEUR: profile.maxLossThresholdEUR ?? DEFAULT_RISK_PROFILE.maxLossThresholdEUR,
      baseRiskPerTradeEUR: profile.baseRiskPerTradeEUR ?? DEFAULT_RISK_PROFILE.baseRiskPerTradeEUR,
      riskRewardRatio: profile.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio,
      winProbabilityPct: profile.winProbabilityPct ?? DEFAULT_RISK_PROFILE.winProbabilityPct,
      targetGoalEUR: profile.targetGoalEUR ?? DEFAULT_RISK_PROFILE.targetGoalEUR,
      planYear: profile.planYear ?? this.selectedPlanYear,
    }, { emitEvent: false });

    this.stepsForm.clear();
    const steps = profile.riskSteps?.length ? profile.riskSteps : DEFAULT_RISK_STEPS;
    steps.forEach((step) => this.addStep(step));
    this.stepsDirty = false;

    this.riskBudget.setProfile(profile);
    this.recalculateTradesNeeded();
  }

  private sortSteps(steps: RiskStep[]): RiskStep[] {
    return [...steps]
      .map((step) => ({
        id: step.id,
        profitMinEUR: Number(step.profitMinEUR ?? 0),
        riskPerTradeEUR: Number(step.riskPerTradeEUR ?? 0),
      }))
      .sort((a, b) => a.profitMinEUR - b.profitMinEUR);
  }

  private recalculateTradesNeeded(): void {
    const ratio = Number(this.profileForm.value.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio ?? 0);
    const winPct = Number(this.profileForm.value.winProbabilityPct ?? DEFAULT_RISK_PROFILE.winProbabilityPct ?? 0);
    const goal = Number(this.profileForm.value.targetGoalEUR ?? DEFAULT_RISK_PROFILE.targetGoalEUR ?? 0);
    const riskPerTrade = this.summary().riskPerTrade;
    const profile = this.lastSummary?.profile ?? DEFAULT_RISK_PROFILE;
    const steps = profile.riskSteps ?? DEFAULT_RISK_STEPS;
    const baseRisk = profile.baseRiskPerTradeEUR ?? DEFAULT_RISK_PROFILE.baseRiskPerTradeEUR;
    let currentProfit = Math.max(this.lastSummary?.positionedPnLEUR ?? 0, 0);

    const remainingGoal = Math.max(goal - currentProfit, 0);
    if (remainingGoal <= 0) {
      this.tradesNeeded.set(0);
      return;
    }

    if (ratio <= 0 || winPct <= 0 || riskPerTrade <= 0) {
      this.tradesNeeded.set(null);
      return;
    }

    const winProb = Math.min(Math.max(winPct / 100, 0), 1);
    let trades = 0;
    const safetyCap = 100000;

    while (currentProfit < goal && trades < safetyCap) {
      const { value: dynamicRisk } = this.riskBudget.calculateRiskPerTrade(currentProfit, baseRisk, steps);
      const expectedPerTrade = dynamicRisk * ((winProb * ratio) - (1 - winProb));
      if (expectedPerTrade <= 0) {
        this.tradesNeeded.set(null);
        return;
      }
      currentProfit += expectedPerTrade;
      trades += 1;
    }

    this.tradesNeeded.set(trades >= safetyCap ? null : trades);
  }

  private buildYearOptions(): number[] {
    const current = new Date();
    const start = 2025;
    const max = current.getFullYear() + (current.getMonth() === 11 ? 1 : 0);
    const years: number[] = [];
    for (let y = start; y <= max; y++) {
      years.push(y);
    }
    return years;
  }

  private async loadPlanYears(): Promise<void> {
    try {
      const ids = await firstValueFrom(this.api.getRiskProfileIds());
      const years = (ids ?? [])
        .map((id) => Number(id))
        .filter((year) => !Number.isNaN(year));
      if (years.length) {
        this.hasProfiles = true;
        this.yearOptions = years;
        if (!years.includes(this.selectedPlanYear)) {
          this.profileForm.patchValue({ planYear: years[0] }, { emitEvent: false });
        }
        return;
      }
    } catch {
      // fallback below
    }

    this.hasProfiles = false;
    this.yearOptions = [];
    this.stepsForm.clear();
    this.currentProfileId = '';
  }

  tradesToNextStep(index: number): number | null {
    const steps = this.sortSteps(this.readProfile().riskSteps);
    if (index < 0 || index >= steps.length - 1) return null;
    const current = steps[index];
    const next = steps[index + 1];
    const ratio = Number(this.profileForm.value.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio ?? 0);
    const winPct = Number(this.profileForm.value.winProbabilityPct ?? DEFAULT_RISK_PROFILE.winProbabilityPct ?? 0);
    if (ratio <= 0 || winPct <= 0) return null;

    const winProb = Math.min(Math.max(winPct / 100, 0), 1);
    const expectedPerTrade = current.riskPerTradeEUR * ((winProb * ratio) - (1 - winProb));
    if (expectedPerTrade <= 0) return null;
    const currentProfit = Math.max(this.lastSummary?.positionedPnLEUR ?? 0, 0);
    const delta = Math.max(next.profitMinEUR - Math.max(current.profitMinEUR, currentProfit), 0);
    return delta > 0 ? Math.ceil(delta / expectedPerTrade) : null;
  }

  onPlanYearChange(year: number): void {
    if (!this.hasProfiles) return;
    this.profileForm.patchValue({ planYear: year }, { emitEvent: false });
    this.loadProfile(year);
    this.loadSteps(String(year));
  }

  get selectedPlanYear(): number {
    return Number(this.profileForm.value.planYear ?? DEFAULT_RISK_PROFILE.planYear ?? new Date().getFullYear());
  }

  openNewProfile(): void {
    this.newProfileOpen = true;
    this.newProfileYear = '';
    this.newProfileError = '';
  }

  cancelNewProfile(): void {
    this.newProfileOpen = false;
    this.newProfileYear = '';
    this.newProfileError = '';
  }

  async saveNewProfile(): Promise<void> {
    const year = Number(this.newProfileYear);
    if (!Number.isFinite(year) || year < 2025) {
      this.newProfileError = 'Bitte ein gültiges Jahr ab 2025 eingeben.';
      return;
    }
    if (this.yearOptions.includes(year)) {
      this.newProfileError = 'Dieses Planungsjahr existiert bereits.';
      return;
    }

    const profile = { ...this.readProfile(), planYear: year, id: String(year) };
    const { riskSteps, ...profilePayload } = profile;
    this.newProfileError = '';
    try {
      const saved = await firstValueFrom(this.api.saveRiskProfile(profilePayload as RiskProfile));
      const updated = saved ? { ...saved, riskSteps } : profile;
      this.hasProfiles = true;
      this.yearOptions = [year, ...this.yearOptions].sort((a, b) => b - a);
      this.currentProfileId = updated.id ?? String(year);
      this.applyProfile(updated);
      this.broadcastProfile();
      await this.loadSteps(this.currentProfileId);
      this.newProfileOpen = false;
      this.newProfileYear = '';
    } catch {
      this.newProfileError = 'Profil konnte nicht gespeichert werden.';
    }
  }

  async deleteProfile(): Promise<void> {
    try {
      await firstValueFrom(this.api.deleteRiskProfile());
      window.location.reload();
    } catch {
      // keep current values
    }
  }

  private async loadProfile(planYear?: number): Promise<void> {
    this.suppressSaves = true;
    try {
      const loaded = await firstValueFrom(this.api.getRiskProfile(planYear));
      if (loaded) {
        this.currentProfileId = loaded.id ?? String(planYear ?? loaded.planYear ?? this.selectedPlanYear);
        this.applyProfile({ ...loaded, planYear: planYear ?? loaded.planYear });
        this.broadcastProfile();
        return;
      }
    } catch {
      // ignore backend errors and use default fallback
    } finally {
      this.suppressSaves = false;
    }

    const fallback: RiskProfile = { ...DEFAULT_RISK_PROFILE, planYear: planYear ?? this.selectedPlanYear };
    this.currentProfileId = fallback.id ?? String(fallback.planYear ?? this.selectedPlanYear);
    this.suppressSaves = true;
    this.applyProfile(fallback);
    this.broadcastProfile();
    this.suppressSaves = false;
  }

  private async loadSteps(profileId: string): Promise<void> {
    this.suppressSaves = true;
    try {
      const steps = await firstValueFrom(this.api.getRiskSteps(profileId));
      if (steps?.length) {
        this.stepsForm.clear();
        this.sortSteps(steps).forEach((step) => this.addStep(step, false));
        this.stepsDirty = false;
        this.broadcastProfile();
        return;
      }
    } catch {
      // fallback below
    } finally {
      this.suppressSaves = false;
    }
    this.suppressSaves = true;
    this.stepsForm.clear();
    DEFAULT_RISK_STEPS.forEach((step) => this.addStep(step, false));
    this.stepsDirty = false;
    this.broadcastProfile();
    this.suppressSaves = false;
  }

  private async persistSteps(steps: RiskStep[]): Promise<RiskStep[]> {
    const profileId = this.profileId;
    const results: RiskStep[] = [];
    for (const step of steps) {
      if (step.id) {
        const updated = await firstValueFrom(this.api.updateRiskStep(step.id, step));
        results.push(updated);
      } else {
        const created = await firstValueFrom(this.api.createRiskStep(step, profileId));
        results.push(created);
      }
    }
    return this.sortSteps(results);
  }

  private async deleteStepRemote(id: number): Promise<void> {
    try {
      await firstValueFrom(this.api.deleteRiskStep(id));
    } catch {
      // ignore remote failure to keep UX smooth
    }
  }

  private get profileId(): string {
    return this.currentProfileId || String(this.selectedPlanYear);
  }
}
